/**
 * Circuit Breaker Pattern for Agent Reliability
 * 
 * Implements adaptive circuit breaking with:
 * - State management (closed, open, half-open)
 * - Failure threshold detection
 * - Automatic recovery attempts
 * - Health monitoring per agent
 * - Performance metrics tracking
 */

/**
 * Circuit breaker states
 */
export enum CircuitState {
  CLOSED = 'closed',      // Normal operation
  OPEN = 'open',          // Failing, reject requests
  HALF_OPEN = 'half_open' // Testing recovery
}

/**
 * Circuit breaker configuration
 */
export interface CircuitBreakerConfig {
  failureThreshold: number;      // Failed requests to open circuit
  successThreshold: number;      // Successful requests to close from half-open
  timeout: number;               // Time in ms before trying to recover
  monitoringPeriod: number;      // Time window for failure tracking
}

/**
 * Circuit breaker health metrics
 */
export interface CircuitHealth {
  state: CircuitState;
  failureCount: number;
  successCount: number;
  totalRequests: number;
  lastFailureTime: number | null;
  lastSuccessTime: number | null;
  nextRetryTime: number | null;
  errorRate: number;
}

/**
 * Circuit Breaker for Agent Execution
 */
export class AgentCircuitBreaker {
  private state: CircuitState = CircuitState.CLOSED;
  private failureCount = 0;
  private successCount = 0;
  private totalRequests = 0;
  private lastFailureTime: number | null = null;
  private lastSuccessTime: number | null = null;
  private nextRetryTime: number | null = null;
  private recentFailures: number[] = []; // Timestamps of recent failures

  constructor(
    private agentId: string,
    private config: CircuitBreakerConfig = {
      failureThreshold: 5,
      successThreshold: 2,
      timeout: 60000, // 1 minute
      monitoringPeriod: 300000 // 5 minutes
    }
  ) {}

  /**
   * Execute function with circuit breaker protection
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    this.totalRequests++;

    // Check if circuit is open
    if (this.state === CircuitState.OPEN) {
      if (this.nextRetryTime && Date.now() >= this.nextRetryTime) {
        console.log(`🔄 Circuit breaker [${this.agentId}] transitioning to HALF_OPEN (testing recovery)`);
        this.state = CircuitState.HALF_OPEN;
        this.successCount = 0;
      } else {
        const waitTime = this.nextRetryTime ? this.nextRetryTime - Date.now() : 0;
        throw new Error(
          `Circuit breaker [${this.agentId}] is OPEN. Retry in ${Math.ceil(waitTime / 1000)}s. ` +
          `Recent failures: ${this.failureCount}/${this.config.failureThreshold}`
        );
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * Handle successful execution
   */
  private onSuccess(): void {
    this.successCount++;
    this.lastSuccessTime = Date.now();

    if (this.state === CircuitState.HALF_OPEN) {
      if (this.successCount >= this.config.successThreshold) {
        console.log(`✅ Circuit breaker [${this.agentId}] transitioning to CLOSED (recovered)`);
        this.state = CircuitState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.nextRetryTime = null;
        this.recentFailures = [];
      }
    } else if (this.state === CircuitState.CLOSED) {
      // Gradually reduce failure count on success
      if (this.failureCount > 0) {
        this.failureCount = Math.max(0, this.failureCount - 1);
      }
    }
  }

  /**
   * Handle failed execution
   */
  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    this.recentFailures.push(Date.now());

    // Clean old failures outside monitoring period
    const cutoff = Date.now() - this.config.monitoringPeriod;
    this.recentFailures = this.recentFailures.filter(time => time > cutoff);

    if (this.state === CircuitState.HALF_OPEN) {
      console.log(`⚠️  Circuit breaker [${this.agentId}] failed during recovery, transitioning to OPEN`);
      this.openCircuit();
    } else if (this.state === CircuitState.CLOSED) {
      if (this.recentFailures.length >= this.config.failureThreshold) {
        console.log(`🚨 Circuit breaker [${this.agentId}] threshold exceeded (${this.recentFailures.length}/${this.config.failureThreshold}), transitioning to OPEN`);
        this.openCircuit();
      }
    }
  }

  /**
   * Open circuit breaker
   */
  private openCircuit(): void {
    this.state = CircuitState.OPEN;
    this.nextRetryTime = Date.now() + this.config.timeout;
    this.successCount = 0;
  }

  /**
   * Get current circuit health
   */
  getHealth(): CircuitHealth {
    const errorRate = this.totalRequests > 0 
      ? this.recentFailures.length / this.totalRequests 
      : 0;

    return {
      state: this.state,
      failureCount: this.recentFailures.length,
      successCount: this.successCount,
      totalRequests: this.totalRequests,
      lastFailureTime: this.lastFailureTime,
      lastSuccessTime: this.lastSuccessTime,
      nextRetryTime: this.nextRetryTime,
      errorRate
    };
  }

  /**
   * Force reset circuit breaker
   */
  reset(): void {
    console.log(`🔄 Circuit breaker [${this.agentId}] manually reset`);
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.totalRequests = 0;
    this.nextRetryTime = null;
    this.recentFailures = [];
  }

  /**
   * Check if circuit allows requests
   */
  isAvailable(): boolean {
    if (this.state === CircuitState.CLOSED) return true;
    if (this.state === CircuitState.HALF_OPEN) return true;
    if (this.state === CircuitState.OPEN && this.nextRetryTime) {
      return Date.now() >= this.nextRetryTime;
    }
    return false;
  }
}

/**
 * Circuit Breaker Manager
 * Manages circuit breakers for all agents
 */
export class CircuitBreakerManager {
  private breakers = new Map<string, AgentCircuitBreaker>();

  /**
   * Get or create circuit breaker for agent
   */
  getBreaker(
    agentId: string,
    config?: Partial<CircuitBreakerConfig>
  ): AgentCircuitBreaker {
    if (!this.breakers.has(agentId)) {
      this.breakers.set(agentId, new AgentCircuitBreaker(agentId, {
        failureThreshold: 5,
        successThreshold: 2,
        timeout: 60000,
        monitoringPeriod: 300000,
        ...config
      }));
    }
    return this.breakers.get(agentId)!;
  }

  /**
   * Execute with circuit breaker protection
   */
  async executeProtected<T>(
    agentId: string,
    fn: () => Promise<T>,
    config?: Partial<CircuitBreakerConfig>
  ): Promise<T> {
    const breaker = this.getBreaker(agentId, config);
    return await breaker.execute(fn);
  }

  /**
   * Get health status for all agents
   */
  getAllHealth(): Record<string, CircuitHealth> {
    const health: Record<string, CircuitHealth> = {};
    
    for (const [agentId, breaker] of this.breakers.entries()) {
      health[agentId] = breaker.getHealth();
    }
    
    return health;
  }

  /**
   * Get health summary
   */
  getHealthSummary(): {
    total: number;
    healthy: number;
    degraded: number;
    failing: number;
  } {
    const health = this.getAllHealth();
    const summary = {
      total: Object.keys(health).length,
      healthy: 0,
      degraded: 0,
      failing: 0
    };

    for (const agentHealth of Object.values(health)) {
      if (agentHealth.state === CircuitState.CLOSED && agentHealth.errorRate < 0.1) {
        summary.healthy++;
      } else if (agentHealth.state === CircuitState.HALF_OPEN || agentHealth.errorRate < 0.5) {
        summary.degraded++;
      } else {
        summary.failing++;
      }
    }

    return summary;
  }

  /**
   * Reset all circuit breakers
   */
  resetAll(): void {
    console.log(`🔄 Resetting all ${this.breakers.size} circuit breakers`);
    for (const breaker of this.breakers.values()) {
      breaker.reset();
    }
  }

  /**
   * Reset specific circuit breaker
   */
  reset(agentId: string): void {
    const breaker = this.breakers.get(agentId);
    if (breaker) {
      breaker.reset();
    }
  }
}

/**
 * Singleton instance
 */
export const circuitBreakerManager = new CircuitBreakerManager();
